import { motion } from "framer-motion";
import { MapPin, TrendingUp, Building2, ArrowUpRight } from "lucide-react";
import { properties } from "@/data/properties";

const getCount = (terms: string[]) =>
  properties.filter(p => terms.some(t => p.location.toLowerCase().includes(t.toLowerCase()))).length;

const markets = [
  { city: "Monaco", country: "Monte Carlo", flag: "🇲🇨", growth: "+12%", avgPrice: "€8.5M", highlights: ["Port Hercules", "Larvotto", "Carré d'Or"] },
  { city: "Dubai", country: "UAE", flag: "🇦🇪", growth: "+18%", avgPrice: "€3.2M", highlights: ["Palm Jumeirah", "Downtown", "Emirates Hills"] },
  { city: "London", country: "United Kingdom", flag: "🇬🇧", growth: "+8%", avgPrice: "€12M", highlights: ["Mayfair", "Knightsbridge", "Chelsea"] },
  { city: "Hong Kong", country: "China SAR", flag: "🇭🇰", growth: "+6%", avgPrice: "HK$180M", highlights: ["The Peak", "Repulse Bay", "Deep Water Bay"] },
  { city: "New York", country: "USA", flag: "🇺🇸", growth: "+10%", avgPrice: "$18M", highlights: ["Manhattan", "Malibu", "Palm Beach"] },
  { city: "Paris", country: "France", flag: "🇫🇷", growth: "+9%", avgPrice: "€6.5M", highlights: ["Haussmann", "Cap-Ferrat", "St-Tropez"] },
  { city: "Riyadh", country: "KSA", flag: "🇸🇦", growth: "+22%", avgPrice: "SAR 35M", highlights: ["Riyadh", "NEOM", "Red Sea"] },
  { city: "Sydney", country: "Australia", flag: "🇦🇺", growth: "+11%", avgPrice: "A$15M", highlights: ["Sydney", "Melbourne", "Byron Bay"] },
  { city: "Zurich", country: "Switzerland", flag: "🇨🇭", growth: "+7%", avgPrice: "CHF 12M", highlights: ["Geneva", "Gstaad", "St. Moritz"] },
  { city: "Doha", country: "Qatar", flag: "🇶🇦", growth: "+16%", avgPrice: "QAR 18M", highlights: ["The Pearl", "Lusail", "West Bay"] },
  { city: "Abu Dhabi", country: "UAE", flag: "🇦🇪", growth: "+14%", avgPrice: "AED 15M", highlights: ["Saadiyat", "Yas Island", "Al Maryah"] },
  { city: "Tel Aviv", country: "Israel", flag: "🇮🇱", growth: "+13%", avgPrice: "₪90M", highlights: ["Tel Aviv", "Caesarea", "Herzliya"] },
  { city: "Macau", country: "China SAR", flag: "🇲🇴", growth: "+9%", avgPrice: "HK$65M", highlights: ["Cotai", "Coloane", "Taipa"] },
  { city: "Tuscany", country: "Italy", flag: "🇮🇹", growth: "+10%", avgPrice: "€4.1M", highlights: ["Amalfi", "Sardinia", "Lake Como"] },
  { city: "Marbella", country: "Spain", flag: "🇪🇸", growth: "+15%", avgPrice: "€2.8M", highlights: ["Golden Mile", "La Zagaleta", "Ibiza"] },
];

const totalProperties = properties.length;
const totalCountries = 15;

const GlobalNetworkSection = () => {
  return (
    <section id="network" className="py-24 lg:py-32 bg-secondary/30 overflow-hidden">
      <div className="container mx-auto px-6 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="font-body text-sm uppercase tracking-[0.3em] text-primary mb-4">
            Global Presence
          </p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-foreground mb-6 leading-tight">
            Connecting Markets
            <span className="block italic font-medium">Across Continents</span>
          </h2>
          <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
            With nearly three decades of experience bridging international markets, we connect discerning investors 
            with extraordinary opportunities across 15 premier global destinations.
          </p>
        </motion.div>

        {/* Market Cards Grid */}
        <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4">
          {markets.map((market, index) => (
            <motion.div
              key={market.city}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              viewport={{ once: true }}
              className="group"
            >
              <div className="bg-card border border-border/50 rounded-xl p-4 h-full transition-all duration-500 hover:border-primary/30 hover:shadow-luxury">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xl">{market.flag}</span>
                  <div>
                    <h3 className="font-display text-sm text-foreground leading-tight">{market.city}</h3>
                    <p className="text-xs text-muted-foreground">{market.country}</p>
                  </div>
                  <div className="ml-auto flex items-center gap-1 text-green-500 text-xs font-medium">
                    <TrendingUp className="w-3 h-3" />
                    {market.growth}
                  </div>
                </div>

                <div className="bg-secondary/50 rounded-lg p-2 mb-3">
                  <p className="text-xs text-muted-foreground">Avg. Price</p>
                  <p className="font-display text-sm text-primary font-semibold">{market.avgPrice}</p>
                </div>

                <div className="flex flex-wrap gap-1">
                  {market.highlights.map((h) => (
                    <span key={h} className="text-[10px] bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                      {h}
                    </span>
                  ))}
                </div>

                <div className="mt-3 pt-3 border-t border-border/30 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="text-xs text-muted-foreground">Explore</span>
                  <ArrowUpRight className="w-3 h-3 text-primary" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <div className="inline-flex items-center gap-8 md:gap-12 bg-card/50 backdrop-blur-sm border border-border/30 rounded-full px-8 py-4">
            <div className="flex items-center gap-3">
              <MapPin className="w-5 h-5 text-primary" />
              <div className="text-left">
                <span className="font-display text-xl text-foreground font-semibold">{totalCountries}</span>
                <p className="text-xs text-muted-foreground">Countries</p>
              </div>
            </div>
            <div className="w-px h-8 bg-border/50" />
            <div className="flex items-center gap-3">
              <Building2 className="w-5 h-5 text-primary" />
              <div className="text-left">
                <span className="font-display text-xl text-foreground font-semibold">{totalProperties}+</span>
                <p className="text-xs text-muted-foreground">Properties</p>
              </div>
            </div>
            <div className="w-px h-8 bg-border/50" />
            <div className="flex items-center gap-3">
              <TrendingUp className="w-5 h-5 text-primary" />
              <div className="text-left">
                <span className="font-display text-xl text-foreground font-semibold">12%</span>
                <p className="text-xs text-muted-foreground">Avg. Growth</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default GlobalNetworkSection;
